import { FormEvent, useState } from "react";

import { PageShell } from "../components/PageShell.js";
import { Seo } from "../components/Seo.js";

type Status = "idle" | "sending" | "sent" | "error";

export function ContactUs() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;

    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get("name") ?? ""),
      email: String(data.get("email") ?? ""),
      phone: String(data.get("phone") ?? ""),
      message: String(data.get("message") ?? ""),
      website: String(data.get("website") ?? ""), // honeypot
    };

    if (!payload.email.trim()) {
      setStatus("error");
      setError("Email is required.");
      return;
    }
    if (!payload.message.trim()) {
      setStatus("error");
      setError("Please enter a message.");
      return;
    }

    setStatus("sending");
    setError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.error || "Something went wrong.");
      }
      form.reset();
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  return (
    <PageShell>
      <Seo
        title="Contact Us · JivaSeva"
        description="Get in touch with JivaSeva to volunteer, collaborate, share ideas, or ask a question. Every message is important to us."
        path="/contact"
      />
      <h1 className="page-heading">Contact Us</h1>
      <p className="manifesto">
        Every message is important to us. As a volunteer-driven initiative,
        responses may sometimes take a little longer, but we'll get back to you
        as soon as we can.
      </p>
      {status === "sent" ? (
        <p className="saved-note" role="status">
          Thank you — your message has been sent. We'll be in touch soon.
        </p>
      ) : (
        <form className="concern-form" onSubmit={handleSubmit} noValidate>
          <label className="field">
            <span>Your name</span>
            <input type="text" name="name" placeholder="Your name" />
          </label>
          <label className="field">
            <span>Email (required)</span>
            <input
              type="email"
              name="email"
              required
              placeholder="you@example.com"
            />
          </label>
          <label className="field">
            <span>Phone (optional)</span>
            <input type="tel" name="phone" placeholder="Optional" />
          </label>
          <label className="field">
            <span>Message</span>
            <textarea
              name="message"
              rows={5}
              placeholder="How can we help?"
            />
          </label>
          {/* Honeypot: hidden from users, catches bots. */}
          <input
            type="text"
            name="website"
            className="hp-field"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          {status === "error" && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="primary-button"
            disabled={status === "sending"}
          >
            {status === "sending" ? "Sending…" : "Send message"}
          </button>
        </form>
      )}
    </PageShell>
  );
}
